import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { AssistantDataService } from './assistant-data.service';
import {
  AssistantResponse,
  Conversation,
  SendMessagePayload
} from '../models/assistant.model';

const CANNED_RESPONSES: Record<string, string> = {
  default:
    'I can help you with drug interaction checks, dosage information, stock queries, and sales summaries. What would you like to know?',
  interaction:
    'Based on the medications listed, there is a known moderate interaction between Metformin and Ibuprofen that may affect kidney function. Monitor renal markers if co-administered.',
  dosage:
    'The standard adult dosage for Amoxicillin is 500mg every 8 hours for 5–7 days. For pediatric patients, dosing is weight-based at 25mg/kg/day divided into 3 doses.',
  stock:
    'Current stock summary: 7 items are below their reorder point, and 3 batches expire within the next 30 days. I recommend creating purchase orders for Metformin 500mg and Atorvastatin 20mg.',
  sales:
    'Over the last 30 days, total sales were ₦4.2M across both branches. Branch 001 accounts for 60% of revenue. Top-selling drug is Amoxicillin 500mg.',
  report:
    'Your P&L for the period shows Gross Revenue: ₦4.2M, COGS: ₦2.5M, Gross Profit: ₦1.7M (40.5% margin). VAT collected: ₦315K.'
};

function pickResponse(content: string): string {
  const lower = content.toLowerCase();
  if (lower.includes('interact')) return CANNED_RESPONSES['interaction'];
  if (lower.includes('dos') || lower.includes('mg') || lower.includes('dose'))
    return CANNED_RESPONSES['dosage'];
  if (
    lower.includes('stock') ||
    lower.includes('inventory') ||
    lower.includes('expir')
  )
    return CANNED_RESPONSES['stock'];
  if (
    lower.includes('sales') ||
    lower.includes('revenue') ||
    lower.includes('sell')
  )
    return CANNED_RESPONSES['sales'];
  if (
    lower.includes('report') ||
    lower.includes('profit') ||
    lower.includes('p&l')
  )
    return CANNED_RESPONSES['report'];
  return CANNED_RESPONSES['default'];
}

@Injectable()
export class InMemoryAssistantDataService extends AssistantDataService {
  private conversations: Conversation[] = [
    {
      id: 'conv-001',
      title: 'Drug interaction check',
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      updatedAt: new Date(Date.now() - 86400000).toISOString(),
      messages: [
        {
          id: 'm-001',
          role: 'user',
          content: 'Check interaction between Metformin and Ibuprofen',
          timestamp: new Date(Date.now() - 86400000).toISOString()
        },
        {
          id: 'm-002',
          role: 'assistant',
          content: CANNED_RESPONSES['interaction'],
          timestamp: new Date(Date.now() - 86300000).toISOString(),
          capability: 'drug_interaction'
        }
      ]
    }
  ];

  getConversations(): Observable<Conversation[]> {
    return of([...this.conversations]).pipe(delay(100));
  }

  getConversation(id: string): Observable<Conversation> {
    return of(this.conversations.find((c) => c.id === id)!).pipe(delay(100));
  }

  createConversation(): Observable<Conversation> {
    const conv: Conversation = {
      id: crypto.randomUUID(),
      title: 'New conversation',
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    this.conversations = [conv, ...this.conversations];
    return of(conv).pipe(delay(100));
  }

  sendMessage(payload: SendMessagePayload): Observable<AssistantResponse> {
    const assistantMsg = {
      id: crypto.randomUUID(),
      role: 'assistant' as const,
      content: pickResponse(payload.content),
      timestamp: new Date().toISOString(),
      capability: 'general' as const
    };
    return of({
      message: assistantMsg,
      conversationId: payload.conversationId
    }).pipe(delay(800));
  }

  deleteConversation(id: string): Observable<void> {
    this.conversations = this.conversations.filter((c) => c.id !== id);
    return of(undefined).pipe(delay(100));
  }
}
